import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Swords, Users, ListOrdered, BarChart3, Crown, Loader2, GitBranch, RefreshCw, Lock, Settings, Play, Check, Trophy, ShieldCheck, Undo2, Copy, Tv, KeyRound, Link2, CalendarDays, Plus, CheckCircle2, Clock } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { labelLineFormat, isDoublesLine, categoryLabel, CW_STATUS_META } from "@/lib/clubwars";
import { CwStandings } from "@/components/CwStandings";
import api, { formatApiError } from "@/lib/api";

const TABS = [
  { id: "overview", label: "Overview", Icon: Swords },
  { id: "fixtures", label: "Fixtures", Icon: GitBranch },
  { id: "standings", label: "Standings", Icon: BarChart3 },
];

export default function ClubWarDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [tab, setTab] = useState("overview");
  const [teamFormat, setTeamFormat] = useState("round_robin");
  const [regenOpen, setRegenOpen] = useState(false);
  const [openFixture, setOpenFixture] = useState(null);
  const [links, setLinks] = useState(null);

  const load = useCallback(() => {
    api.get(`/club-wars/${id}/view`).then(({ data }) => { setEvent(data); if (data.team_format) setTeamFormat(data.team_format); })
      .catch(() => { toast.error("Could not load this Club War"); nav("/club-wars"); });
    api.get(`/club-wars/${id}/captain-links`).then(({ data }) => setLinks(data)).catch(() => {});
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { api.get(`/club-wars/${id}/view`).then(({ data }) => setEvent(data)).catch(() => {}); }, 4000);
    return () => clearInterval(t);
  }, [id]);

  const generate = async () => {
    try { const { data } = await api.post(`/club-wars/${id}/fixtures`, { team_format: teamFormat }); setEvent(data); setRegenOpen(false); setTab("fixtures"); toast.success("Fixtures generated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not generate fixtures"); }
  };

  const addEntry = async (lineId) => {
    try { const { data } = await api.post(`/club-wars/${id}/lines/add-entry`, { line_id: lineId }); setEvent(data); toast.success("Extra entry added to this category"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add entry"); }
  };

  const teamName = (tid) => (event?.teams || []).find((t) => t.id === tid)?.name || (tid === "__BYE__" ? "BYE" : "TBD");
  const refresh = (data) => { if (data) setEvent(data); else load(); };

  if (!event) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;

  const hasFixtures = (event.fixtures || []).length > 0;
  const current = openFixture ? (event.fixtures || []).find((f) => f.fixture_id === openFixture.fixture_id) : null;
  const totalTeams = (event.teams || []).length;
  const submittedTeams = (event.teams || []).filter((t) => t.roster_locked).length;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title={event.name} />
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-10 grain">
        <button data-testid="cwd-back" onClick={() => nav("/club-wars")} className="text-sm flex items-center gap-2 mb-6" style={{ color: "var(--text-2)" }}>
          <ArrowLeft className="w-4 h-4" /> All Club Wars
        </button>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h1 className="font-display font-bold tracking-tight text-3xl flex items-center gap-3 min-w-0"><Swords className="w-7 h-7 text-primary-c shrink-0" /> <span className="min-w-0 break-words">{event.name}</span></h1>
            <div className="flex flex-wrap gap-3 mt-2 text-sm" style={{ color: "var(--text-2)" }}>
              {event.venue && <span>{event.venue}</span>}
              <span className="flex items-center gap-1"><Users className="w-4 h-4" />{(event.teams || []).length} teams</span>
              <span className="flex items-center gap-1"><ListOrdered className="w-4 h-4" />{(event.lines || []).length} categories</span>
              <span className="flex items-center gap-1"><GitBranch className="w-4 h-4" />{event.courts || 1} courts</span>
              <span className="flex items-center gap-1">{event.umpires_enabled ? <><ShieldCheck className="w-4 h-4 text-primary-c" /> Umpired</> : "Self-officiated"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="cwd-edit-setup" onClick={() => nav(`/club-wars/${id}/edit`)} className="glass rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Settings className="w-4 h-4 text-primary-c" /> Edit setup</button>
            <button data-testid="cwd-schedule-btn" onClick={() => nav(`/club-wars/${id}/schedule`)} className="glass rounded-full px-4 py-2.5 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><CalendarDays className="w-4 h-4 text-primary-c" /> Schedule</button>
          </div>
        </div>

        <div className="flex gap-1 glass rounded-full p-1 w-fit mb-8" data-testid="cwd-tabs">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`cwd-tab-${t.id}`} onClick={() => setTab(t.id)}
              className="rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 transition-colors"
              style={tab === t.id ? { background: "var(--primary)", color: "var(--on-primary)" } : { color: "var(--text-2)" }}><t.Icon className="w-4 h-4" /> {t.label}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="space-y-6" data-testid="cwd-overview">
            <div className="rounded-2xl glow-border p-6" style={{ background: "var(--surface)" }}>
              <h3 className="font-display font-bold text-lg mb-3">Team bracket</h3>
              {totalTeams > 0 && (
                <div data-testid="cwd-roster-summary" className={`text-xs mb-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 ${submittedTeams === totalTeams ? "text-green-400" : ""}`} style={{ background: "var(--bg)", color: submittedTeams === totalTeams ? "#22c55e" : "var(--text-2)" }}>
                  {submittedTeams === totalTeams ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                  {submittedTeams}/{totalTeams} teams submitted their final roster{submittedTeams === totalTeams ? " — ready to generate" : ""}
                </div>
              )}
              {!hasFixtures ? (
                <div className="space-y-4">
                  <p className="text-sm" style={{ color: "var(--text-2)" }}>Generate fixtures so your clubs face off. Each fixture plays all {(event.lines || []).length} configured lines.</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <Select value={teamFormat} onValueChange={setTeamFormat}>
                      <SelectTrigger data-testid="cwd-team-format" className="w-56 rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}><SelectValue /></SelectTrigger>
                      <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
                        <SelectItem value="round_robin" data-testid="cwd-team-format-round_robin">Round Robin (league)</SelectItem>
                        <SelectItem value="single_elim" data-testid="cwd-team-format-single_elim">Single Elimination (knockout)</SelectItem>
                      </SelectContent>
                    </Select>
                    <button data-testid="cwd-generate-fixtures" onClick={generate} disabled={(event.teams || []).length < 2 || (event.lines || []).length < 1}
                      className="btn-glow rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-2 disabled:opacity-50"><GitBranch className="w-4 h-4" /> Generate fixtures</button>
                  </div>
                  {(event.teams || []).length < 2 && <p className="text-xs text-amber-400">Add at least 2 teams in Edit setup.</p>}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-sm" style={{ color: "var(--text-2)" }}>Format: <span className="font-bold" style={{ color: "var(--text)" }}>{event.team_format === "single_elim" ? "Single Elimination" : "Round Robin"}</span> · {(event.fixtures || []).length} fixtures</span>
                  <button data-testid="cwd-regenerate" onClick={() => setRegenOpen(true)} className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}><RefreshCw className="w-3.5 h-3.5 text-primary-c" /> Regenerate</button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cwd-teams">
              {(event.teams || []).map((t) => (
                <div key={t.id} className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-display font-bold truncate">{t.name}</div>
                    {t.roster_locked
                      ? <span data-testid={`cwd-roster-badge-${t.id}`} className="shrink-0 inline-flex items-center gap-1 text-[10px] mono uppercase rounded-full px-2 py-0.5" style={{ background: "rgba(34,197,94,0.14)", color: "#22c55e" }}><CheckCircle2 className="w-3 h-3" /> Roster in</span>
                      : <span data-testid={`cwd-roster-badge-${t.id}`} className="shrink-0 inline-flex items-center gap-1 text-[10px] mono uppercase rounded-full px-2 py-0.5" style={{ background: "rgba(245,158,11,0.14)", color: "#f59e0b" }}><Clock className="w-3 h-3" /> Awaiting</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(t.players || []).map((p) => <span key={p.id} className="glass rounded-full px-2.5 py-0.5 text-xs">{p.name}</span>)}
                    {(t.players || []).length === 0 && <span className="text-xs" style={{ color: "var(--text-2)" }}>No players yet</span>}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl glow-border p-6" style={{ background: "var(--surface)" }} data-testid="cwd-categories">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-lg flex items-center gap-2"><ListOrdered className="w-5 h-5 text-primary-c" /> Match categories</h3>
                <span className="text-xs" style={{ color: "var(--text-2)" }}>Add extra entries (e.g. a 2nd pair)</span>
              </div>
              <div className="space-y-2">
                {(event.lines || []).map((ln) => (
                  <div key={ln.id} data-testid={`cwd-category-${ln.id}`} className="flex items-center justify-between rounded-xl glow-border px-4 py-2.5" style={{ background: "var(--bg)" }}>
                    <span className="text-sm font-medium">{categoryLabel(ln)} <span className="text-xs" style={{ color: "var(--text-2)" }}>· {ln.entries || 1} {(ln.entries || 1) > 1 ? "entries" : "entry"}</span></span>
                    <button data-testid={`cwd-add-entry-${ln.id}`} onClick={() => addEntry(ln.id)} disabled={(ln.entries || 1) >= 8} className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 disabled:opacity-40" style={{ color: "var(--text)" }}><Plus className="w-3.5 h-3.5 text-primary-c" /> Add entry</button>
                  </div>
                ))}
                {(event.lines || []).length === 0 && <p className="text-sm" style={{ color: "var(--text-2)" }}>No categories configured — add them in Edit setup.</p>}
              </div>
            </div>

            <div className="rounded-2xl glow-border p-6" style={{ background: "var(--surface)" }} data-testid="cwd-sharing">
              <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Link2 className="w-5 h-5 text-primary-c" /> Sharing & access</h3>
              <div className="space-y-4">
                <ShareRow icon={Tv} label="TV / spectator board" testid="cwd-tv-link"
                  url={event.public_token ? `${window.location.origin}/cw/tv/${event.public_token}` : ""} />
                {(links?.captains || []).map((c) => (
                  <ShareRow key={c.team_id} icon={Users} label={`${c.team_name} — captain link`} testid={`cwd-captain-link-${c.team_id}`}
                    url={`${window.location.origin}/cw/captain/${c.token}`} />
                ))}
                <a href="/cw-umpire" target="_blank" rel="noreferrer" className="text-xs text-primary-c font-bold flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Court umpire PIN login opens at /cw-umpire</a>
              </div>
            </div>
          </div>
        )}

        {tab === "fixtures" && (
          <div data-testid="cwd-fixtures">
            {!hasFixtures && <div className="rounded-2xl glow-border p-10 text-center" style={{ background: "var(--surface)" }}><p className="text-sm" style={{ color: "var(--text-2)" }}>No fixtures yet — generate them from the Overview tab.</p></div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(event.fixtures || []).map((fx) => {
                const st = CW_STATUS_META[fx.status] || { l: fx.status, color: "#94a3b8" };
                const isBye = fx.team_a === "__BYE__" || fx.team_b === "__BYE__";
                return (
                  <div key={fx.fixture_id} data-testid={`cwd-fixture-${fx.fixture_id}`} className="rounded-2xl glow-border p-5 flex flex-col" style={{ background: "var(--surface)" }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] mono uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{fx.round_label}</span>
                      <span className="text-[10px] mono uppercase rounded-full px-2 py-0.5" style={{ background: `${st.color}22`, color: st.color }}>{st.l}</span>
                    </div>
                    <div className="space-y-1.5">
                      <Row name={teamName(fx.team_a)} score={fx.score?.a} win={fx.winner === fx.team_a} />
                      <Row name={teamName(fx.team_b)} score={fx.score?.b} win={fx.winner === fx.team_b} />
                    </div>
                    {!isBye && (
                      <button data-testid={`cwd-manage-${fx.fixture_id}`} onClick={() => setOpenFixture(fx)} className="mt-4 btn-glow rounded-full py-2 text-sm font-bold flex items-center justify-center gap-2"><ListOrdered className="w-4 h-4" /> Line-ups & scoring</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "standings" && (
          <div className="rounded-2xl glow-border overflow-hidden" style={{ background: "var(--surface)" }} data-testid="cwd-standings">
            <div className="px-4 py-3 font-display font-bold flex items-center gap-2 border-b" style={{ borderColor: "var(--border)" }}><BarChart3 className="w-4 h-4 text-primary-c" /> Live standings</div>
            <div className="px-2 pb-2"><CwStandings rows={event.standings || []} variant="compact" /></div>
          </div>
        )}
      </div>

      {current && (
        <FixtureDialog cwId={id} fixture={current} event={event} teamName={teamName} onClose={() => setOpenFixture(null)} onUpdate={refresh} />
      )}

      <AlertDialog open={regenOpen} onOpenChange={setRegenOpen}>
        <AlertDialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="cwd-regen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Regenerate fixtures?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--text-2)" }}>This clears all current fixtures, line-ups and recorded line scores for this event.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cwd-regen-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="cwd-regen-confirm" onClick={(e) => { e.preventDefault(); generate(); }} className="bg-red-600 hover:bg-red-700 text-white">Regenerate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ShareRow({ icon: Icon, label, url, testid }) {
  if (!url) return null;
  return (
    <div>
      <div className="text-xs font-bold mb-1 flex items-center gap-1.5" style={{ color: "var(--text-2)" }}><Icon className="w-3.5 h-3.5 text-primary-c" /> {label}</div>
      <div className="flex items-center gap-2">
        <input readOnly data-testid={testid} value={url} className="flex-1 rounded-lg bg-surface glow-border px-3 py-2 text-xs mono truncate" style={{ color: "var(--text)" }} />
        <button data-testid={`${testid}-copy`} onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }} className="glass rounded-lg px-3 py-2 shrink-0" style={{ color: "var(--text)" }}><Copy className="w-4 h-4 text-primary-c" /></button>
        <a href={url} target="_blank" rel="noreferrer" className="glass rounded-lg px-3 py-2 shrink-0 flex items-center" style={{ color: "var(--text)" }}><Link2 className="w-4 h-4 text-primary-c" /></a>
      </div>
    </div>
  );
}

function Row({ name, score, win }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`flex items-center gap-1.5 ${win ? "font-bold text-primary-c" : ""}`}>{win && <Crown className="w-3.5 h-3.5" style={{ color: "#eab308" }} />}{name}</span>
      <span className="mono font-bold">{score ?? 0}</span>
    </div>
  );
}

function FixtureDialog({ cwId, fixture, event, teamName, onClose, onUpdate }) {
  const teamA = fixture.team_a, teamB = fixture.team_b;
  const bothLocked = (fixture.locked || {})[teamA] && (fixture.locked || {})[teamB];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="cwd-fixture-dialog" className="glass max-w-2xl max-h-[88vh] overflow-y-auto" style={{ background: "var(--surface-glass)" }}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Swords className="w-5 h-5 text-primary-c" /> {teamName(teamA)} vs {teamName(teamB)}</DialogTitle>
          <DialogDescription style={{ color: "var(--text-2)" }}>{bothLocked ? "Record each line's score to decide the fixture." : "Assign and lock both line-ups to start scoring."}</DialogDescription>
        </DialogHeader>
        {!bothLocked ? (
          <div className="space-y-5">
            <p className="text-sm" style={{ color: "var(--text-2)" }}>Assign players to each line for both teams, then lock. A player can only play one line in this fixture.</p>
            {[teamA, teamB].map((tid) => (
              <LineupEditor key={tid} cwId={cwId} fixture={fixture} event={event} teamId={tid} teamName={teamName(tid)} onUpdate={onUpdate} />
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="cwd-scoring">
            {(fixture.line_matches || []).map((lm) => (
              <LineScorer key={lm.lm_id} cwId={cwId} lm={lm} umpired={event.umpires_enabled} courts={event.courts || 1} teamAName={teamName(teamA)} teamBName={teamName(teamB)} bestOf={event.best_of} onUpdate={onUpdate} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LineupEditor({ cwId, fixture, event, teamId, teamName, onUpdate }) {
  const team = (event.teams || []).find((t) => t.id === teamId);
  const roster = team?.players || [];
  const locked = (fixture.locked || {})[teamId];
  const init = (fixture.lineups || {})[teamId] || {};
  const slots = [];
  (event.lines || []).forEach((ln) => {
    const entries = Math.max(1, ln.entries || 1);
    for (let i = 1; i <= entries; i++) slots.push({ slot_key: `${ln.id}:${i}`, type: ln.type, skill: ln.skill, entry_label: entries > 1 ? `#${i}` : "" });
  });
  const [draft, setDraft] = useState(() => {
    const d = {};
    slots.forEach((s) => { d[s.slot_key] = init[s.slot_key] || []; });
    return d;
  });
  const [busy, setBusy] = useState(false);

  const setSlot = (key, slot, pid) => setDraft((d) => {
    const arr = [...(d[key] || [])]; arr[slot] = pid; return { ...d, [key]: arr };
  });

  const lockIt = async () => {
    setBusy(true);
    const assignments = {};
    Object.entries(draft).forEach(([k, arr]) => { assignments[k] = (arr || []).filter(Boolean); });
    try {
      await api.post(`/club-wars/${cwId}/fixtures/${fixture.fixture_id}/lineup`, { team_id: teamId, assignments });
      const { data } = await api.post(`/club-wars/${cwId}/fixtures/${fixture.fixture_id}/lineup/lock`, { team_id: teamId });
      toast.success(`${teamName} line-up locked`);
      onUpdate(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not lock line-up"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl glow-border p-4" style={{ background: "var(--surface)" }} data-testid={`cwd-lineup-${teamId}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-display font-bold">{teamName}</span>
        {locked ? <span className="text-[10px] mono uppercase text-primary-c flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>
          : <button data-testid={`cwd-lock-${teamId}`} onClick={lockIt} disabled={busy} className="btn-glow rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />} Lock line-up</button>}
      </div>
      <div className="space-y-2">
        {slots.map((sl) => {
          const n = isDoublesLine(sl.type) ? 2 : 1;
          return (
            <div key={sl.slot_key} className="flex flex-wrap items-center gap-2">
              <span className="text-xs w-44 shrink-0" style={{ color: "var(--text-2)" }}>{categoryLabel(sl)}</span>
              {Array.from({ length: n }).map((_, s) => (
                <select key={s} data-testid={`cwd-slot-${teamId}-${sl.slot_key}-${s}`} disabled={locked} value={draft[sl.slot_key]?.[s] || ""}
                  onChange={(e) => setSlot(sl.slot_key, s, e.target.value)}
                  className="rounded-lg glow-border bg-surface px-2 py-1.5 text-sm outline-none disabled:opacity-60" style={{ color: "var(--text)" }}>
                  <option value="">— player —</option>
                  {roster.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LineScorer({ cwId, lm, umpired, courts, teamAName, teamBName, bestOf, onUpdate }) {
  const [sc, setSc] = useState(null);
  const [serving, setServing] = useState("a");
  const [busy, setBusy] = useState(false);
  const [sa, setSa] = useState("");
  const [sb, setSb] = useState("");
  const [minutes, setMinutes] = useState("");
  const [pin, setPin] = useState(null);
  const isFinal = lm.status === "final";
  const sets = lm.sets || [];

  const makePin = async () => {
    try { const { data } = await api.post(`/club-wars/${cwId}/pins`, { lm_id: lm.lm_id, label: categoryLabel(lm) }); setPin(data.pin); toast.success("Court PIN created"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create PIN"); }
  };
  const setCourt = async (val) => {
    try { const { data } = await api.post(`/club-wars/${cwId}/lines/${lm.lm_id}/court`, { court: val ? Number(val) : null }); onUpdate(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not assign court"); }
  };

  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/club-wars/${cwId}/lines/${lm.lm_id}/start`, { serving_team: serving }); setSc(data.scorecard); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start"); }
    finally { setBusy(false); }
  };
  const rally = async (action) => {
    try { const { data } = await api.post(`/club-wars/${cwId}/lines/${lm.lm_id}/rally`, { action }); setSc(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const undo = async () => { try { const { data } = await api.post(`/club-wars/${cwId}/lines/${lm.lm_id}/undo`); setSc(data); } catch (e) { toast.error("Nothing to undo"); } };
  const commit = async () => {
    try { const { data } = await api.post(`/club-wars/${cwId}/lines/${lm.lm_id}/set/commit`); setSc(null); onUpdate(data); toast.success("Set committed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const record = async () => {
    const a = Number(sa), b = Number(sb);
    if ((sa === "" && sb === "") || Number.isNaN(a) || Number.isNaN(b)) { toast.error("Enter this set's score"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/club-wars/${cwId}/lines/${lm.lm_id}/set/record`, { a, b, minutes: minutes ? Number(minutes) : undefined }); onUpdate(data); setSa(""); setSb(""); setMinutes(""); toast.success("Set recorded"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not record set"); }
    finally { setBusy(false); }
  };

  const nameA = (lm.a_player_names || []).join(" / ") || teamAName;
  const nameB = (lm.b_player_names || []).join(" / ") || teamBName;
  const bo3 = lm.format === "bo3";

  if (lm.status === "dead") {
    return (
      <div className="rounded-xl glow-border p-4 opacity-60" style={{ background: "var(--surface)" }} data-testid={`cwd-line-dead-${lm.lm_id}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold">{categoryLabel(lm)}</span>
          <span className="text-[10px] mono uppercase" style={{ color: "var(--text-2)" }}>not needed · series decided</span>
        </div>
        <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>{nameA} vs {nameB}</div>
      </div>
    );
  }

  return (
    <div className="rounded-xl glow-border p-4" style={{ background: "var(--surface)" }} data-testid={`cwd-line-${lm.lm_id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-bold">{categoryLabel(lm)}</span>
        <div className="flex items-center gap-2">
          <select data-testid={`cwd-court-${lm.lm_id}`} value={lm.court || ""} onChange={(e) => setCourt(e.target.value)} className="text-[10px] mono rounded-full glow-border bg-surface px-2 py-0.5 outline-none" style={{ color: "var(--text)" }}>
            <option value="">No court</option>
            {Array.from({ length: courts || 1 }).map((_, i) => <option key={i} value={i + 1}>Court {i + 1}</option>)}
          </select>
          {umpired && !isFinal && ((lm.pin || pin)
            ? <span data-testid={`cwd-pin-${lm.lm_id}`} className="text-xs mono font-black tracking-widest text-primary-c glass rounded-full px-2.5 py-0.5">{lm.pin || pin}</span>
            : <button data-testid={`cwd-make-pin-${lm.lm_id}`} onClick={makePin} className="text-[10px] mono uppercase glass rounded-full px-2 py-0.5 flex items-center gap-1" style={{ color: "var(--text)" }}><KeyRound className="w-3 h-3 text-primary-c" /> PIN</button>)}
          <span className="text-[10px] mono uppercase" style={{ color: "var(--text-2)" }}>{labelLineFormat(lm.format)}{umpired ? " · umpired" : " · self"}</span>
        </div>
      </div>
      <div className="text-xs mb-3" style={{ color: "var(--text-2)" }}>{nameA} <span className="mono">vs</span> {nameB}</div>

      {isFinal ? (
        <div className="flex items-center justify-between rounded-lg glass px-3 py-2" data-testid={`cwd-line-final-${lm.lm_id}`}>
          <span className="flex items-center gap-1.5 font-bold"><Crown className="w-4 h-4" style={{ color: "#eab308" }} /> {lm.winner === "a" ? nameA : nameB}</span>
          <span className="mono text-sm">{sets.map((s) => `${s.a}-${s.b}`).join(", ")}</span>
        </div>
      ) : umpired ? (
        sc ? (
          <div className="space-y-3" data-testid={`cwd-rally-${lm.lm_id}`}>
            <div className="flex items-center justify-center gap-6 py-2">
              <div className="text-center"><div className="text-[10px] uppercase" style={{ color: "var(--text-2)" }}>{nameA}</div><div className="font-display font-black text-4xl">{sc.team_a_score}</div></div>
              <div className="text-center"><div className="mono text-sm text-primary-c">{sc.score_call}</div><div className="text-[10px]" style={{ color: "var(--text-2)" }}>serving {sc.serving_team === "a" ? "A" : "B"} · {sc.server_position}</div></div>
              <div className="text-center"><div className="text-[10px] uppercase" style={{ color: "var(--text-2)" }}>{nameB}</div><div className="font-display font-black text-4xl">{sc.team_b_score}</div></div>
            </div>
            <div className="text-[11px] text-center" style={{ color: "var(--text-2)" }}>{sc.explanation}</div>
            {sc.match_over ? (
              <button data-testid={`cwd-commit-${lm.lm_id}`} onClick={commit} className="w-full btn-glow rounded-full py-2.5 text-sm font-bold flex items-center justify-center gap-2"><Check className="w-4 h-4" /> Commit set ({sc.team_a_score}-{sc.team_b_score})</button>
            ) : (
              <div className="flex gap-2">
                <button data-testid={`cwd-point-${lm.lm_id}`} onClick={() => rally("POINT")} className="flex-1 btn-glow rounded-full py-2.5 text-sm font-bold">POINT</button>
                <button data-testid={`cwd-fault-${lm.lm_id}`} onClick={() => rally("FAULT")} className="flex-1 glass rounded-full py-2.5 text-sm font-bold" style={{ color: "var(--text)" }}>FAULT</button>
                <button data-testid={`cwd-undo-${lm.lm_id}`} onClick={undo} disabled={!sc.can_undo} className="glass rounded-full px-3 disabled:opacity-40" style={{ color: "var(--text)" }}><Undo2 className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {sets.length > 0 && <span className="text-xs mono" style={{ color: "var(--text-2)" }}>sets: {sets.map((s) => `${s.a}-${s.b}`).join(", ")}</span>}
            <span className="text-xs" style={{ color: "var(--text-2)" }}>First serve:</span>
            <select data-testid={`cwd-serving-${lm.lm_id}`} value={serving} onChange={(e) => setServing(e.target.value)} className="rounded-lg glow-border bg-surface px-2 py-1 text-sm outline-none" style={{ color: "var(--text)" }}>
              <option value="a">{nameA}</option><option value="b">{nameB}</option>
            </select>
            <button data-testid={`cwd-start-${lm.lm_id}`} onClick={start} disabled={busy} className="btn-glow rounded-full px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 ml-auto"><Play className="w-3.5 h-3.5" /> {sets.length ? "Next set" : "Start set"}</button>
          </div>
        )
      ) : (
        <div className="space-y-2" data-testid={`cwd-entry-${lm.lm_id}`}>
          {sets.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {sets.map((s, i) => <span key={i} data-testid={`cwd-set-chip-${lm.lm_id}-${i}`} className="glass rounded-full px-2.5 py-0.5 text-xs mono">Set {i + 1}: {s.a}-{s.b}</span>)}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs w-12" style={{ color: "var(--text-2)" }}>Set {sets.length + 1}</span>
            <input data-testid={`cwd-set-a-${lm.lm_id}`} type="number" min="0" value={sa} onChange={(e) => setSa(e.target.value)} placeholder={nameA} className="w-20 rounded-lg glow-border bg-surface px-2 py-1.5 text-sm outline-none" style={{ color: "var(--text)" }} />
            <span className="mono">-</span>
            <input data-testid={`cwd-set-b-${lm.lm_id}`} type="number" min="0" value={sb} onChange={(e) => setSb(e.target.value)} placeholder={nameB} className="w-20 rounded-lg glow-border bg-surface px-2 py-1.5 text-sm outline-none" style={{ color: "var(--text)" }} />
            {sets.length === 0 && <input data-testid={`cwd-minutes-${lm.lm_id}`} type="number" min="0" value={minutes} onChange={(e) => setMinutes(e.target.value)} placeholder="mins" className="w-20 rounded-lg glow-border bg-surface px-2 py-1 text-xs outline-none" style={{ color: "var(--text)" }} />}
            <button data-testid={`cwd-record-set-${lm.lm_id}`} onClick={record} disabled={busy} className="btn-glow rounded-full px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 ml-auto disabled:opacity-50">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trophy className="w-3.5 h-3.5" />} Record set</button>
          </div>
          <p className="text-[11px]" style={{ color: "var(--text-2)" }}>Each recorded set updates the live leaderboard instantly{bo3 ? ` · best of ${bestOf}` : ""}.</p>
        </div>
      )}
    </div>
  );
}
